import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable } from 'rxjs';
import { SchemaService } from 'src/app/shared/symptoms/services/schema/schema.service';
import { ChipColour } from '../../../../chip/chip.component';
import { DryEyeFormGroup } from '../../dry-eye-form/dry-eye-form.model';
import { DewsIIIService, HomeostasisResult } from '../dews-iii.service';
import { DRIVER_SUB_META, DriverSubMeta } from '../dews-iii-drivers/dews-iii-drivers.component';
import {
  ALL_DEWS_SIGN_KEYS,
  DRIVER_SIGN_SCHEMA,
  HomeostasisCategoryKey,
  HOMEOSTASIS_SIGN_SCHEMA
} from '../dews-iii-sign-model';
import {
  DRIVER_THRESHOLDS,
  HOMEOSTASIS_THRESHOLDS,
  getDriverKeys
} from '../dews-iii-drivers/driver-thresholds';

@Component({
  selector: 'csi-dews-iii-signs',
  templateUrl: 'dews-iii-signs.component.html',
  styleUrls: ['dews-iii-signs.component.scss']
})
export class DewsIIISignsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formGroup: DryEyeFormGroup;
  @Input() assessment: any;
  @Input() onQuestionnaireLinked$: Observable<any>;
  @Input() previousDryEyeForm: { [key: string]: any } | null = null;
  @Input() previousAssessmentDate: Date | null = null;
  @Input() signKeysToShow: string[] | null = null;

  readonly allSignKeys: string[] = ALL_DEWS_SIGN_KEYS;
  layoutFilteredSignKeys: string[] = ALL_DEWS_SIGN_KEYS.slice();

  // Filter state — precomputed for template (no function calls in template)
  activeHomeostasisKeys: { [key: string]: boolean } = {};
  activeDriverKeys: { [key: string]: boolean } = {};
  hasActiveFilter = false;
  visibleSigns: { [signKey: string]: boolean } = {};

  // Homeostasis panel
  readonly homeostasisCategoryKeys: HomeostasisCategoryKey[] = [
    'tearFilmStability',
    'osmolarity',
    'ocularSurfaceStaining'
  ];
  readonly homeostasisCategoryMeta: {
    [K in HomeostasisCategoryKey]: { code: string; title: string };
  } = {
    tearFilmStability: { code: '3A', title: 'Tear Film Stability' },
    osmolarity: { code: '3B', title: 'Tear Osmolarity' },
    ocularSurfaceStaining: { code: '3C', title: 'Ocular Surface Staining' }
  };
  categoryStatusLabels: { [key: string]: string } = {};
  categoryStatusChipColours: { [key: string]: ChipColour } = {};
  private categoryResults: { [key: string]: HomeostasisResult } = {};

  // Driver panel
  readonly driverCategoryKeys: Array<'tearFilm' | 'eyelidAnomaly' | 'ocularSurface'> = [
    'tearFilm',
    'eyelidAnomaly',
    'ocularSurface'
  ];
  readonly driverCategoryLabels: { [key: string]: string } = {
    tearFilm: 'Tear Film',
    eyelidAnomaly: 'Eyelid Anomaly',
    ocularSurface: 'Ocular Surface'
  };
  readonly allDriversMeta: DriverSubMeta[] = DRIVER_SUB_META;
  driversByCategory: { [key: string]: DriverSubMeta[] } = {};
  driverChipTexts: { [controlKey: string]: string } = {};
  driverChipColours: { [controlKey: string]: ChipColour } = {};
  driverIdentified: { [key: string]: boolean } = {};

  signFlags: { [contextKey: string]: boolean } = {};
  signFlagDescriptions: { [contextKey: string]: string } = {};
  signHasData: { [contextKey: string]: boolean } = {};
  signKeyContextKeys: { [signKey: string]: string[] } = {};
  private userOverrides: { [driverKey: string]: boolean } = {};
  private previousAutoIdentifyState: { [driverKey: string]: boolean } = {};
  private subscribedSignControlKeys: { [key: string]: boolean } = {};

  constructor(public schemaService: SchemaService, private dewsIIIService: DewsIIIService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.signKeysToShow) {
      const keys = this.signKeysToShow;
      this.layoutFilteredSignKeys = keys
        ? keys.filter(k => ALL_DEWS_SIGN_KEYS.indexOf(k) > -1)
        : ALL_DEWS_SIGN_KEYS.slice();
    }
  }

  ngOnInit() {
    this.driverCategoryKeys.forEach(cat => {
      this.driversByCategory[cat] = DRIVER_SUB_META.filter(m => m.category === cat);
    });

    ALL_DEWS_SIGN_KEYS.forEach(signKey => {
      const seen: { [k: string]: boolean } = {};
      const contextKeys: string[] = [];
      DRIVER_THRESHOLDS.concat(HOMEOSTASIS_THRESHOLDS).forEach(t => {
        if (t.signKey === signKey && t.canAutoFlag) {
          const ck = t.driverContext + ':' + signKey;
          if (!seen[ck]) {
            seen[ck] = true;
            contextKeys.push(ck);
          }
        }
      });
      this.signKeyContextKeys[signKey] = contextKeys;
    });

    this.ensureControls();
    this.initCategoryResults();
    this.updateCategoryDisplay();
    this.updateDriverState();

    setTimeout(() => {
      this.evaluateAllCategories();
      this.evaluateAndAutoIdentify();
      this.subscribeToSignChanges();
    });

    if (this.onQuestionnaireLinked$) {
      this.onQuestionnaireLinked$.pipe(untilDestroyed(this)).subscribe(() => {
        this.evaluateAllCategories();
        this.evaluateAndAutoIdentify();
      });
    }

    const overrideControl = this.formGroup.controls['dewsClinicianOverride'];
    if (overrideControl) {
      overrideControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
        setTimeout(() => {
          this.evaluateAndAutoIdentify();
        });
      });
    }
  }

  ngOnDestroy() {}

  selectFilter(type: 'homeostasis' | 'driver', key: string): void {
    if (type === 'homeostasis') {
      if (this.activeHomeostasisKeys[key]) {
        const next = { ...this.activeHomeostasisKeys };
        delete next[key];
        this.activeHomeostasisKeys = next;
      } else {
        this.activeHomeostasisKeys = { ...this.activeHomeostasisKeys, [key]: true };
      }
    } else {
      if (this.activeDriverKeys[key]) {
        const next = { ...this.activeDriverKeys };
        delete next[key];
        this.activeDriverKeys = next;
      } else {
        this.activeDriverKeys = { ...this.activeDriverKeys, [key]: true };
      }
    }
    this.hasActiveFilter =
      Object.keys(this.activeHomeostasisKeys).length > 0 ||
      Object.keys(this.activeDriverKeys).length > 0;
    this.recomputeVisibleSigns();
  }

  clearAllFilters(): void {
    this.activeHomeostasisKeys = {};
    this.activeDriverKeys = {};
    this.hasActiveFilter = false;
    this.visibleSigns = {};
  }

  private recomputeVisibleSigns(): void {
    const next: { [signKey: string]: boolean } = {};
    const homeostasisSchema = HOMEOSTASIS_SIGN_SCHEMA as { [k: string]: string[] };
    const driverSchema = DRIVER_SIGN_SCHEMA as { [k: string]: string[] };

    Object.keys(this.activeHomeostasisKeys).forEach(key => {
      const signs = homeostasisSchema[key] || [];
      signs.forEach(sign => {
        next[sign] = true;
      });
    });

    Object.keys(this.activeDriverKeys).forEach(key => {
      const signs = driverSchema[key] || [];
      signs.forEach(sign => {
        next[sign] = true;
      });
    });

    this.visibleSigns = next;
  }

  // ─── Homeostasis Evaluation ───────────────────────────────────────────────

  private initCategoryResults(): void {
    const categories: HomeostasisCategoryKey[] = [
      'tearFilmStability',
      'osmolarity',
      'ocularSurfaceStaining'
    ];
    categories.forEach(category => {
      this.categoryResults[category] = { category: category, performed: false, positive: false };
    });

    if (this.formGroup.controls['dewsHomeostasisResults']) {
      const saved = this.formGroup.controls['dewsHomeostasisResults'].value;
      if (saved && Array.isArray(saved)) {
        saved.forEach((result: HomeostasisResult) => {
          if (this.categoryResults[result.category]) {
            this.categoryResults[result.category] = result;
          }
        });
      }
    } else {
      this.formGroup.addControl(
        'dewsHomeostasisResults',
        new FormControl({ value: null, disabled: this.formGroup.disabled })
      );
    }
  }

  evaluateAllCategories(): void {
    this.evaluateTearFilmStability();
    this.evaluateOsmolarity();
    this.evaluateOcularSurfaceStaining();
    this.saveHomeostasisResults();
    this.updateCategoryDisplay();
  }

  private evaluateTearFilmStability(): void {
    const controls = this.formGroup.controls;
    let hasData = false;
    let isPositive = false;

    const rightNibut = controls['RIGHT_NITBUT'] && controls['RIGHT_NITBUT'].value;
    const leftNibut = controls['LEFT_NITBUT'] && controls['LEFT_NITBUT'].value;
    const rightItbut = controls['RIGHT_ITBUT'] && controls['RIGHT_ITBUT'].value;
    const leftItbut = controls['LEFT_ITBUT'] && controls['LEFT_ITBUT'].value;

    if (rightNibut !== null && rightNibut !== undefined) {
      hasData = true;
      if (this.dewsIIIService.evaluateNibut(rightNibut)) {
        isPositive = true;
      }
    }
    if (leftNibut !== null && leftNibut !== undefined) {
      hasData = true;
      if (this.dewsIIIService.evaluateNibut(leftNibut)) {
        isPositive = true;
      }
    }
    if (rightItbut !== null && rightItbut !== undefined) {
      hasData = true;
      if (this.dewsIIIService.evaluateFbut(rightItbut)) {
        isPositive = true;
      }
    }
    if (leftItbut !== null && leftItbut !== undefined) {
      hasData = true;
      if (this.dewsIIIService.evaluateFbut(leftItbut)) {
        isPositive = true;
      }
    }

    this.categoryResults['tearFilmStability'] = {
      category: 'tearFilmStability',
      performed: hasData,
      positive: isPositive
    };
  }

  private evaluateOsmolarity(): void {
    const controls = this.formGroup.controls;
    const rightVal = controls['RIGHT_OSMOLARITY'] && controls['RIGHT_OSMOLARITY'].value;
    const leftVal = controls['LEFT_OSMOLARITY'] && controls['LEFT_OSMOLARITY'].value;
    const hasData =
      (rightVal !== null && rightVal !== undefined) || (leftVal !== null && leftVal !== undefined);

    let higherEye = rightVal;
    let lowerEye = leftVal;
    if (rightVal !== null && rightVal !== undefined && leftVal !== null && leftVal !== undefined) {
      higherEye = Math.max(rightVal, leftVal);
      lowerEye = Math.min(rightVal, leftVal);
    }

    const isPositive =
      hasData &&
      this.dewsIIIService.evaluateOsmolarity(
        higherEye !== null && higherEye !== undefined ? higherEye : 0,
        lowerEye !== null && lowerEye !== undefined ? lowerEye : 0
      );

    this.categoryResults['osmolarity'] = {
      category: 'osmolarity',
      performed: hasData,
      positive: isPositive
    };
  }

  private evaluateOcularSurfaceStaining(): void {
    const controls = this.formGroup.controls;
    let hasData = false;
    let isPositive = false;

    ['FLUORESCEIN_STAINING', 'CORNEAL_STAINING_PATTERN', 'CORNEAL_STAINING_EXTENT', 'LWE'].forEach(
      function(key) {
        const rightVal = controls['RIGHT_' + key] && controls['RIGHT_' + key].value;
        const leftVal = controls['LEFT_' + key] && controls['LEFT_' + key].value;
        if (rightVal !== null && rightVal !== undefined) {
          hasData = true;
        }
        if (leftVal !== null && leftVal !== undefined) {
          hasData = true;
        }
      }
    );

    const rightStaining =
      controls['RIGHT_FLUORESCEIN_STAINING'] && controls['RIGHT_FLUORESCEIN_STAINING'].value;
    const leftStaining =
      controls['LEFT_FLUORESCEIN_STAINING'] && controls['LEFT_FLUORESCEIN_STAINING'].value;
    if (rightStaining && rightStaining !== 'grade0' && rightStaining !== 'noStaining') {
      isPositive = true;
    }
    if (leftStaining && leftStaining !== 'grade0' && leftStaining !== 'noStaining') {
      isPositive = true;
    }

    ['RIGHT_CORNEAL_STAINING_EXTENT', 'LEFT_CORNEAL_STAINING_EXTENT'].forEach(function(controlKey) {
      const val = controls[controlKey] && controls[controlKey].value;
      if (val !== null && val !== undefined && val !== 'grade0' && val !== 'normal') {
        isPositive = true;
      }
    });

    ['RIGHT_LWE', 'LEFT_LWE'].forEach(function(controlKey) {
      const val = controls[controlKey] && controls[controlKey].value;
      if (val !== null && val !== undefined && val !== 'noLidWiper' && val !== 'grade0') {
        isPositive = true;
      }
    });

    this.categoryResults['ocularSurfaceStaining'] = {
      category: 'ocularSurfaceStaining',
      performed: hasData,
      positive: isPositive
    };
  }

  private saveHomeostasisResults(): void {
    const results: HomeostasisResult[] = [
      this.categoryResults['tearFilmStability'],
      this.categoryResults['osmolarity'],
      this.categoryResults['ocularSurfaceStaining']
    ];
    if (this.formGroup.controls['dewsHomeostasisResults']) {
      this.formGroup.controls['dewsHomeostasisResults'].setValue(results);
    }
  }

  private updateCategoryDisplay(): void {
    ['tearFilmStability', 'osmolarity', 'ocularSurfaceStaining'].forEach(category => {
      const result = this.categoryResults[category];
      if (!result || !result.performed) {
        this.categoryStatusLabels[category] = 'Not Performed';
        this.categoryStatusChipColours[category] = 'grey-no-outline';
      } else {
        this.categoryStatusLabels[category] = result.positive ? 'Positive' : 'Negative';
        this.categoryStatusChipColours[category] = result.positive
          ? 'red-no-outline'
          : 'green-no-outline';
      }
    });
  }

  // ─── Driver Auto-Identification ───────────────────────────────────────────

  private ensureControls(): void {
    const driverControls = [
      'dewsDriverLipid',
      'dewsDriverAqueous',
      'dewsDriverMucinGlycocalyx',
      'dewsDriverBlinkMechanism',
      'dewsDriverLidMargin',
      'dewsDriverAnatomicalAlignment',
      'dewsDriverNeuralDysfunction',
      'dewsDriverCellDamage',
      'dewsDriverInflammation',
      'dewsDriverNotes'
    ];
    driverControls.forEach(key => {
      if (!this.formGroup.controls[key]) {
        this.formGroup.addControl(
          key,
          new FormControl({ value: null, disabled: this.formGroup.disabled })
        );
      }
    });
  }

  private subscribeToSignChanges(): void {
    ALL_DEWS_SIGN_KEYS.forEach(signKey => {
      [
        'RIGHT_' + signKey,
        'LEFT_' + signKey,
        'RIGHT_' + signKey + '_METHOD',
        'LEFT_' + signKey + '_METHOD'
      ].forEach(controlKey => {
        if (this.subscribedSignControlKeys[controlKey]) {
          return;
        }
        const ctrl = this.formGroup.controls[controlKey];
        if (ctrl) {
          ctrl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.evaluateAllCategories();
            this.evaluateAndAutoIdentify();
          });
          this.subscribedSignControlKeys[controlKey] = true;
        }
      });
    });
  }

  private evaluateAndAutoIdentify(): void {
    this.evaluateSignFlags();
    this.autoIdentifyDrivers();
    this.updateDriverState();
  }

  isSignFlagged(driverContext: string, signKey: string): boolean {
    return this.signFlags[driverContext + ':' + signKey] === true;
  }

  getSignFlagDescription(driverContext: string, signKey: string): string {
    return this.signFlagDescriptions[driverContext + ':' + signKey] || '';
  }

  private evaluateSignFlags(): void {
    const controls = this.formGroup.controls;
    DRIVER_THRESHOLDS.concat(HOMEOSTASIS_THRESHOLDS).forEach(threshold => {
      const contextKey = threshold.driverContext + ':' + threshold.signKey;
      if (!threshold.canAutoFlag) {
        this.signFlags[contextKey] = false;
        this.signHasData[contextKey] = false;
        this.signFlagDescriptions[contextKey] = '';
        return;
      }
      const hasData = threshold.hasData(controls);
      const flagged = hasData && threshold.evaluate(controls);
      this.signHasData[contextKey] = hasData;
      this.signFlags[contextKey] = flagged;
      this.signFlagDescriptions[contextKey] = flagged ? threshold.description : '';
    });
  }

  private autoIdentifyDrivers(): void {
    const controls = this.formGroup.controls;
    let changed = false;
    getDriverKeys().forEach(driverKey => {
      const thresholds = DRIVER_THRESHOLDS.filter(t => t.driverKey === driverKey && t.canAutoFlag);
      if (thresholds.length === 0) {
        return;
      }

      let filledCount = 0;
      let anyFilledMeetsThreshold = false;
      thresholds.forEach(t => {
        const contextKey = t.driverContext + ':' + t.signKey;
        if (this.signHasData[contextKey]) {
          filledCount++;
          if (this.signFlags[contextKey]) {
            anyFilledMeetsThreshold = true;
          }
        }
      });

      const shouldAutoIdentify = filledCount > 0 && anyFilledMeetsThreshold;
      if (!shouldAutoIdentify) {
        delete this.userOverrides[driverKey];
      }

      if (shouldAutoIdentify && !this.userOverrides[driverKey]) {
        const ctrl = controls[driverKey];
        if (ctrl && ctrl.value !== true) {
          ctrl.setValue(true, { emitEvent: false });
          changed = true;
        }
      } else if (
        !shouldAutoIdentify &&
        this.previousAutoIdentifyState[driverKey] &&
        !this.userOverrides[driverKey]
      ) {
        const ctrl = controls[driverKey];
        if (ctrl && ctrl.value === true) {
          ctrl.setValue(false, { emitEvent: false });
          changed = true;
        }
      }

      this.previousAutoIdentifyState[driverKey] = shouldAutoIdentify;
    });

    if (changed) {
      this.formGroup.updateValueAndValidity();
    }
  }

  private updateDriverState(): void {
    const controls = this.formGroup.controls;
    getDriverKeys().forEach(key => {
      this.driverIdentified[key] = controls[key] && controls[key].value === true;
    });
    DRIVER_SUB_META.forEach(meta => {
      const identified = this.driverIdentified[meta.controlKey];
      this.driverChipTexts[meta.controlKey] = identified ? 'Identified' : 'Not Identified';
      this.driverChipColours[meta.controlKey] = identified ? 'orange' : 'grey-no-outline';
    });
  }
}
